import { Link } from 'react-router-dom';
import { useState } from 'react';

const NEWSLETTER_FORM_URL = 'https://readdy.ai/api/form/d99l492g3iubr452inb0';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('company_alt') as string || '').trim();
    if (honeypot) {
      setSubmitStatus('success');
      setEmail('');
      setTimeout(() => setSubmitStatus('idle'), 3000);
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      const response = await fetch(NEWSLETTER_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: formData.get('email') as string }).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setSubmitStatus('success');
        setEmail('');
        setTimeout(() => setSubmitStatus('idle'), 3000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || 'Something went wrong. Please try again.';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setSubmitStatus('success');
          setEmail('');
          setTimeout(() => setSubmitStatus('idle'), 3000);
        } else {
          setFormError(serverMsg);
          setSubmitStatus('error');
        }
      }
    } catch {
      setFormError('Network error. Please check your connection.');
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-background-100 border-t border-background-200/60">
      <div className="w-full px-4 md:px-6 py-14 md:py-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
            <div className="lg:col-span-2">
              <Link to="/" className="inline-block mb-4">
                <span className="font-heading text-xl font-bold text-foreground-950">
                  Hipobuy <span className="text-accent-500">Spreadsheet</span>
                </span>
              </Link>
              <p className="text-sm text-foreground-500 leading-relaxed mb-6 max-w-sm">
                The ultimate spreadsheet shopping resource hub. We help shoppers discover, organize and compare products efficiently through comprehensive category guides and expert resources.
              </p>
              <div className="bg-background-50 rounded-lg p-4 border border-background-200/50">
                <p className="text-xs font-semibold text-foreground-700 mb-2 uppercase tracking-wider">Newsletter</p>
                <p className="text-xs text-foreground-500 mb-3">Get the latest spreadsheet updates and shopping guides.</p>
                <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-2" data-readdy-form="true">
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email address"
                    required
                    className="flex-1 px-4 py-2.5 rounded-full border border-background-200 bg-white text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-accent-300 transition-colors"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-5 py-2.5 bg-accent-500 text-background-50 rounded-full text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap disabled:opacity-60"
                  >
                    {submitting ? 'Sending...' : 'Subscribe'}
                  </button>
                  <div className="form-honeypot-wrapper">
                    <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                </form>
                {submitStatus === 'success' && (
                  <p className="text-xs text-green-600 mt-2">Thanks for subscribing! Check your inbox soon.</p>
                )}
                {submitStatus === 'error' && (
                  <p className="text-xs text-red-500 mt-2">{formError || 'Something went wrong.'}</p>
                )}
              </div>
            </div>

            <div>
              <h5 className="text-xs font-semibold text-foreground-700 mb-4 uppercase tracking-wider">Spreadsheet</h5>
              <ul className="space-y-2.5">
                <li><a href="/" className="text-sm text-foreground-500 hover:text-foreground-950 transition-colors">Home</a></li>
                <li><a href="#categories" className="text-sm text-foreground-500 hover:text-foreground-950 transition-colors">Categories</a></li>
                <li><a href="#trending" className="text-sm text-foreground-500 hover:text-foreground-950 transition-colors">Trending</a></li>
                <li><a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-500 hover:text-foreground-950 transition-colors">Main Store</a></li>
              </ul>
            </div>

            <div>
              <h5 className="text-xs font-semibold text-foreground-700 mb-4 uppercase tracking-wider">Guides</h5>
              <ul className="space-y-2.5">
                <li><Link to="/article/hipobuy-spreadsheet-guide" className="text-sm text-foreground-500 hover:text-foreground-950 transition-colors">Hipobuy Guide</Link></li>
                <li><a href="/article/hipobuy-spreadsheet-guide#faq" className="text-sm text-foreground-500 hover:text-foreground-950 transition-colors">FAQ</a></li>
                <li><a href="/article/hipobuy-spreadsheet-guide#safety" className="text-sm text-foreground-500 hover:text-foreground-950 transition-colors">Shopping Safety</a></li>
              </ul>
            </div>

            <div>
              <h5 className="text-xs font-semibold text-foreground-700 mb-4 uppercase tracking-wider">Legal</h5>
              <ul className="space-y-2.5">
                <li><a href="#" className="text-sm text-foreground-500 hover:text-foreground-950 transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="text-sm text-foreground-500 hover:text-foreground-950 transition-colors">Terms of Use</a></li>
                <li><a href="#" className="text-sm text-foreground-500 hover:text-foreground-950 transition-colors">Safety Tips</a></li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-6 border-t border-background-200/60 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4 text-xs text-foreground-400">
              <span>&copy; {new Date().getFullYear()} Hipobuy Spreadsheet. All rights reserved.</span>
              <span className="hidden sm:inline text-foreground-300">|</span>
              <a href="#" className="hover:text-foreground-600 transition-colors">Privacy</a>
              <span className="text-foreground-300">|</span>
              <a href="#" className="hover:text-foreground-600 transition-colors">Terms</a>
            </div>
            <div className="flex items-center gap-3">
              <a href="#" aria-label="Twitter" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 hover:bg-accent-100 hover:text-accent-600 transition-colors">
                <i className="ri-twitter-x-line"></i>
              </a>
              <a href="#" aria-label="Pinterest" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 hover:bg-accent-100 hover:text-accent-600 transition-colors">
                <i className="ri-pinterest-line"></i>
              </a>
              <a href="#" aria-label="Reddit" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 hover:bg-accent-100 hover:text-accent-600 transition-colors">
                <i className="ri-reddit-line"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}